import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Bot, FileText, Sparkles, MoreVertical, Reply, Share2, Copy, Undo2, Trash2, SmilePlus, IdCard, CreditCard, Phone, Play,
  AlertCircle, UserRound, Zap, Smartphone, Mail, HelpCircle, BarChart3, CheckCircle2, Loader2,
} from 'lucide-react'
import { cn, initials } from '@/lib/utils'
import { safeHttpUrl } from '@/lib/safe-url'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { describeMessage, type MessageView } from './message-content'
import { usePollDetail, type ChatMessage } from '@/hooks/use-conversations'
import { sourceCaption, sourceOf, type SourceIcon } from './message-source'
import { useAiBots } from '@/hooks/use-ai'
import { defaultAiName } from '@/lib/ai-khung'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import {
  fileAttachments,
  formatClock,
  imageAttachments,
  parseContent,
  proxyZaloMedia,
  stickerUrlFromAttachments,
  stickerUrlFromContent,
} from './lib'
import { ContactProfileMenu } from './contact-profile-menu'

/**
 * Cảm xúc nhanh — backend (`zalo-reactions.ts`) nhận CHÍNH EMOJI, không phải mã
 * nội bộ của zca-js; chỉ 6 emoji này được hỗ trợ. Chuỗi rỗng = gỡ cảm xúc.
 */
const QUICK_REACTIONS: { icon: string; label: string }[] = [
  { icon: '❤️', label: '❤️' },
  { icon: '👍', label: '👍' },
  { icon: '😂', label: '😂' },
  { icon: '😮', label: '😮' },
  { icon: '😢', label: '😢' },
  { icon: '😡', label: '😡' },
]

export interface MessageActions {
  onReply: (m: ChatMessage) => void
  onForward: (m: ChatMessage) => void
  onReact: (m: ChatMessage, icon: string) => void
  onUndo: (m: ChatMessage) => void
  onDelete: (m: ChatMessage) => void
}

/** Bong bóng tin nhắn: hệ thống ở giữa, khách bên trái, mình bên phải. */

/**
 * Cảm xúc đã thả lên một tin nhắn — gom theo emoji kèm số lượt, giống Zalo.
 *
 * Trước đây phần này KHÔNG được vẽ: backend nhận và lưu đủ, API cũng trả về,
 * nhưng giao diện bỏ qua hoàn toàn nên khách thả tim mà nhân viên không thấy gì.
 */
function MessageReactions({ reactions }: {
  reactions?: { emoji: string; reactorId: string; reactorName?: string | null }[]
}) {
  if (!reactions?.length) return null

  // Gom theo emoji, giữ danh sách TÊN người thả để hiện tooltip (như Zalo).
  const grouped = new Map<string, string[]>()
  for (const r of reactions) {
    const who = r.reactorName || `zlw${r.reactorId}`
    grouped.set(r.emoji, [...(grouped.get(r.emoji) ?? []), who])
  }

  return (
    <div className="-mt-1.5 flex flex-wrap gap-1 px-1">
      {[...grouped.entries()].map(([emoji, names]) => (
        <span
          key={emoji}
          title={names.join(', ')}
          className="inline-flex cursor-default items-center gap-0.5 rounded-full border bg-card px-1.5 py-0.5 text-[11px] leading-none shadow-sm"
        >
          <span className="text-[13px]">{emoji}</span>
          {names.length > 1 && <span className="text-muted-foreground">{names.length}</span>}
        </span>
      ))}
    </div>
  )
}

/** Người đã xem tới tin này — chat-panel tính từ `conversation.seenBy`. */
export interface SeenViewer {
  name: string
  avatarUrl?: string | null
}

export function MessageBubble({
  message,
  actions,
  seenViewers,
  senderAvatarUrl,
  accountId,
  convId,
}: {
  message: ChatMessage
  actions?: MessageActions
  seenViewers?: SeenViewer[]
  /** Ảnh đại diện người gửi (chỉ hội thoại NHÓM) — hiện cạnh tên giống Zalo gốc. */
  senderAvatarUrl?: string | null
  /** channelAccountId của tài khoản Zalo cá nhân — có thì tên người gửi bấm được (menu Zalo gốc). */
  accountId?: string
  /** Hội thoại hiện tại — cần để tải chi tiết bình chọn thật (`PollCardBubble`). */
  convId?: string
}) {
  const { t } = useTranslation('convChat')
  const isSelf = message.senderType === 'self'
  const isSystem = message.senderType === 'system'
  const view = describeMessage(message)

  // Sự kiện nhóm, cuộc gọi, nhắc sinh nhật… tới từ Zalo với senderType
  // 'contact'/'self' nhưng bản chất là thông báo — vẽ chip giữa màn như Zalo.
  if (isSystem || view.kind === 'system' || view.kind === 'poll') {
    return (
      <div className="my-2 flex flex-col items-center gap-1.5">
        <span className="rounded-full bg-muted px-3 py-1 text-xs text-muted-foreground">
          {view.kind === 'system' || view.kind === 'poll' ? view.text : message.content || t('bubble.systemEvent')}
        </span>
        {view.kind === 'poll' && convId && <PollCardBubble convId={convId} pollId={view.pollId} />}
      </div>
    )
  }

  const parsed = parseContent(message.content, message.contentType)
  const images = [...imageAttachments(message.attachments)]
  if (parsed.imageUrl) images.push(parsed.imageUrl)
  // Ảnh CDN Zalo phải đi qua proxy cùng origin, nếu không ở production bị CSP chặn.
  const displayImages = images.map((u) => proxyZaloMedia(u) ?? u)
  const files = fileAttachments(message.attachments)
  const isSticker = message.contentType === 'sticker'
  // `parseContent` trả NGUYÊN chuỗi JSON khi không nhận ra dạng ảnh/tệp — đó
  // chính là đường JSON lọt ra màn hình. Khi tin không kèm media thì lấy bản đã
  // diễn giải; khi có media thì phần chữ vốn đã trống nên giữ nguyên.
  const hasMedia = images.length > 0 || files.length > 0 || !!parsed.fileHref
  const bodyText = view.kind === 'text' && !hasMedia ? view.text : parsed.text

  return (
    <div
      className={cn(
        'group/msg flex w-full items-center gap-1',
        isSelf ? 'justify-end' : 'justify-start',
      )}
    >
      {/* Menu thao tác — bên trái với tin của mình */}
      {isSelf && actions && !message.isDeleted && (
        <MessageMenu message={message} actions={actions} isSelf />
      )}

      <div className={cn('flex min-w-0 max-w-[78%] flex-col gap-1', isSelf ? 'items-end' : 'items-start')}>
        {!isSelf && message.senderName && (() => {
          const senderRow = (
            <span className="flex items-center gap-1 px-1 text-xs font-medium text-muted-foreground">
              <Avatar className="h-4 w-4 shrink-0">
                {safeHttpUrl(senderAvatarUrl) && <AvatarImage src={safeHttpUrl(senderAvatarUrl)} alt="" />}
                <AvatarFallback className="text-[8px]">{initials(message.senderName)}</AvatarFallback>
              </Avatar>
              {message.senderName}
            </span>
          )
          // Bấm tên/avatar → menu "Xem trang cá nhân / Nhắn tin riêng / Thêm bạn" giống Zalo gốc — chỉ
          // khi biết CẢ accountId (Zalo cá nhân, do chat-panel truyền xuống) lẫn uid người gửi.
          if (!accountId || !message.senderUid) return senderRow
          return (
            <ContactProfileMenu accountId={accountId} uid={message.senderUid} name={message.senderName} avatarUrl={senderAvatarUrl}>
              <button type="button" className="rounded hover:bg-muted/60">{senderRow}</button>
            </ContactProfileMenu>
          )
        })()}

        {message.isDeleted ? (
          <div
            className={cn(
              'rounded-2xl border border-dashed px-3.5 py-2 text-sm italic text-muted-foreground',
              isSelf ? 'rounded-br-sm' : 'rounded-bl-sm',
            )}
          >
            {t('bubble.recalled')}
          </div>
        ) : message.visibility === 'internal' || message.visibility === 'private' ? (
          /* Tin NỘI BỘ / RIÊNG TƯ — style tách biệt hẳn tin gửi khách */
          <div
            className={cn(
              'rounded-2xl border px-3.5 py-2 text-sm shadow-sm',
              isSelf ? 'rounded-br-sm' : 'rounded-bl-sm',
              message.visibility === 'internal'
                ? 'border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100'
                : 'border-violet-300 bg-violet-50 text-violet-950 dark:border-violet-700 dark:bg-violet-950/40 dark:text-violet-100',
            )}
          >
            <div className="mb-1 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide opacity-80">
              {message.visibility === 'internal' ? `🔒 ${t('bubble.internal')}` : `🔐 ${t('bubble.private')}`}
              <span className="font-medium normal-case tracking-normal">
                · {message.repliedBy?.fullName || message.senderName || t('bubble.staff')}
              </span>
            </div>
            {message.reply && (
              <div className="mb-1.5 rounded-md border-l-2 border-current/40 bg-black/5 px-2 py-1 text-xs opacity-75 dark:bg-white/5">
                {message.reply.content?.slice(0, 120)}
              </div>
            )}
            <div className="whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{message.content}</div>
          </div>
        ) : isSticker ? (
          <StickerBubble content={message.content} attachments={message.attachments} />
        ) : message.contentType === 'card' ? (
          <CardBubble content={message.content} isSelf={isSelf} />
        ) : message.contentType === 'bank_card' ? (
          <BankCardBubble content={message.content} isSelf={isSelf} />
        ) : view.kind === 'link' ? (
          // Trước `contentType === 'link'`: tin Zalo có link cũng mang loại này (bản mới) — LinkBubble chỉ vẽ 2 dòng.
          <ZaloLinkBubble view={view} reply={message.reply?.content} isSelf={isSelf} />
        ) : message.contentType === 'link' ? (
          <LinkBubble content={message.content} isSelf={isSelf} />
        ) : view.kind === 'video' ? (
          <VideoBubble view={view} isSelf={isSelf} />
        ) : view.kind === 'card' ? (
          <GenericCardBubble view={view} isSelf={isSelf} />
        ) : (
          <div
            className={cn(
              // max-w-full: chữ `truncate` (tên tệp dài) không được ép bong bóng rộng hơn khung tin (lỗi tràn ngang trên iPhone).
              'max-w-full overflow-hidden rounded-2xl text-sm shadow-sm',
              isSelf
                ? 'rounded-br-sm bg-primary/10 text-foreground'
                : 'rounded-bl-sm bg-muted text-foreground',
              images.length || files.length ? 'p-1.5' : 'px-3.5 py-2',
            )}
          >

            {message.reply?.content && (
              <div
                className={cn(
                  'mb-1 border-l-2 px-2 py-1 text-xs opacity-80',
                  isSelf ? 'border-primary/40' : 'border-border',
                )}
              >
                {message.reply.content}
              </div>
            )}

            {displayImages.map((src, i) => (
              <a key={i} href={safeHttpUrl(src)} target="_blank" rel="noreferrer" className="block">
                <img
                  src={src}
                  alt={t('bubble.image')}
                  loading="lazy"
                  referrerPolicy="no-referrer"
                  className="mb-1 max-h-64 w-full rounded-lg object-cover"
                />
              </a>
            ))}

            {files.map((f, i) => (
              <a
                key={i}
                href={safeHttpUrl(f.url)}
                target="_blank"
                rel="noreferrer"
                className={cn(
                  'my-0.5 flex min-w-0 max-w-full items-center gap-2 rounded-lg px-2.5 py-2 text-sm underline-offset-2 hover:underline',
                  'bg-background',
                )}
              >
                <FileText className="h-4 w-4 shrink-0" />
                <span className="truncate">{f.title || t('bubble.attachment')}</span>
              </a>
            ))}

            {safeHttpUrl(parsed.fileHref) && (
              <a
                href={safeHttpUrl(parsed.fileHref)}
                target="_blank"
                rel="noreferrer"
                className={cn(
                  'my-0.5 flex min-w-0 max-w-full items-center gap-2 rounded-lg px-2.5 py-2 text-sm hover:underline',
                  'bg-background',
                )}
              >
                <FileText className="h-4 w-4 shrink-0" />
                <span className="truncate">{parsed.fileTitle}</span>
              </a>
            )}

            {bodyText && (
              <p className="whitespace-pre-wrap break-words px-2 py-0.5 [overflow-wrap:anywhere]">
                <LinkifiedText text={bodyText} />
              </p>
            )}
          </div>
        )}

        <MessageReactions reactions={message.reactions} />

        <span className="flex items-center gap-1 px-1 text-[11px] text-muted-foreground">
          {formatClock(message.sentAt)}
          {/* Ai / cái gì gửi tin này — chỉ nội bộ thấy. Tin nội bộ / riêng tư đã ghi tên ở đầu khung. */}
          {isSelf && message.visibility !== 'internal' && message.visibility !== 'private' && (
            <SourceTag message={message} />
          )}
        </span>

        {/* Tin nằm trong CRM nhưng khách KHÔNG nhận được — phải nói rõ, nếu
            không nhân viên tưởng đã gửi (xem Message.sendError ở backend). */}
        {isSelf && message.sendError && (
          <span
            role="alert"
            className="flex max-w-full items-start gap-1 rounded-md bg-destructive/10 px-2 py-1 text-[11px] font-medium text-destructive"
          >
            <AlertCircle className="mt-px h-3.5 w-3.5 shrink-0" />
            <span>{t('bubble.sendFailed', { error: message.sendError })}</span>
          </span>
        )}

        {/* "Đã xem" như Zalo: avatar nhỏ dưới TIN CUỐI mà mỗi người đã xem tới */}
        {isSelf && seenViewers && seenViewers.length > 0 && (
          <div
            className="-mt-0.5 flex items-center gap-1 px-1"
            title={t('bubble.seenBy', { names: seenViewers.map((v) => v.name).join(', ') })}
          >
            <span className="text-[10px] text-muted-foreground">{t('bubble.seen')}</span>
            <div className="flex -space-x-1">
              {seenViewers.slice(0, 6).map((v, i) => (
                <Avatar key={i} className="h-3.5 w-3.5 border border-background">
                  {v.avatarUrl && <AvatarImage src={v.avatarUrl} alt="" />}
                  <AvatarFallback className="text-[7px]">{initials(v.name)}</AvatarFallback>
                </Avatar>
              ))}
            </div>
            {seenViewers.length > 6 && (
              <span className="text-[10px] text-muted-foreground">+{seenViewers.length - 6}</span>
            )}
          </div>
        )}
      </div>

      {/* Menu thao tác — bên phải với tin của khách */}
      {!isSelf && actions && !message.isDeleted && (
        <MessageMenu message={message} actions={actions} isSelf={false} />
      )}
    </div>
  )
}

/** Menu thao tác trên từng tin nhắn — chỉ hiện khi rê chuột vào tin */
function MessageMenu({
  message,
  actions,
  isSelf,
}: {
  message: ChatMessage
  actions: MessageActions
  isSelf: boolean
}) {
  const { t } = useTranslation('convChat')
  // Tin Zalo có link / có định dạng: chép phần chữ khách gõ, không chép chuỗi JSON đã lưu.
  const view = describeMessage(message)
  const copyText = view.kind === 'link' || (message.contentType === 'text' && view.kind === 'text') ? view.text : null
  const canCopy = !!copyText

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t('menu.actions')}
          className={cn(
            'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-muted-foreground',
            'opacity-0 transition-opacity hover:bg-muted focus:opacity-100 group-hover/msg:opacity-100',
            'data-[state=open]:opacity-100',
          )}
        >
          <MoreVertical className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align={isSelf ? 'end' : 'start'} className="w-52">
        {/* Hàng cảm xúc nhanh */}
        <div className="flex items-center gap-0.5 px-1.5 py-1.5">
          {QUICK_REACTIONS.map((r) => (
            <button
              key={r.icon}
              type="button"
              title={t('menu.react', { emoji: r.label })}
              onClick={() => actions.onReact(message, r.icon)}
              className="flex h-7 w-7 items-center justify-center rounded-full text-base transition-transform hover:scale-110 hover:bg-muted"
            >
              {r.label}
            </button>
          ))}
          <button
            type="button"
            title={t('menu.removeReaction')}
            onClick={() => actions.onReact(message, '')}
            className="flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground hover:bg-muted"
          >
            <SmilePlus className="h-3.5 w-3.5" />
          </button>
        </div>
        <DropdownMenuSeparator />

        <DropdownMenuItem onClick={() => actions.onReply(message)}>
          <Reply className="mr-2 h-4 w-4" />
          {t('menu.reply')}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => actions.onForward(message)}>
          <Share2 className="mr-2 h-4 w-4" />
          {t('menu.forward')}
        </DropdownMenuItem>
        {canCopy && (
          <DropdownMenuItem onClick={() => navigator.clipboard.writeText(copyText ?? '')}>
            <Copy className="mr-2 h-4 w-4" />
            {t('common:actions.copy')}
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />
        {isSelf && (
          <DropdownMenuItem onClick={() => actions.onUndo(message)} className="text-destructive">
            <Undo2 className="mr-2 h-4 w-4" />
            {t('menu.recall')}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => actions.onDelete(message)} className="text-destructive">
          <Trash2 className="mr-2 h-4 w-4" />
          {t('menu.deleteForMe')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function StickerBubble({ content, attachments }: { content: string | null; attachments?: unknown }) {
  // Backend lưu sticker dạng URL, JSON {"id":...} hoặc chuỗi số → quy về URL CDN Zalo; sticker Zalo OA (bản ghi cũ
  // nội dung rỗng) lấy link trong phần đính kèm.
  const url = stickerUrlFromContent(content) ?? stickerUrlFromAttachments(attachments)
  const { t } = useTranslation('convChat')
  const [failed, setFailed] = useState(false)

  if (url && !failed) {
    return (
      <img
        src={url}
        alt={t('bubble.sticker')}
        loading="lazy"
        className="h-28 w-28 object-contain"
        onError={() => setFailed(true)}
      />
    )
  }
  return (
    <div className="flex items-center gap-1 rounded-2xl bg-muted px-3 py-2 text-sm text-muted-foreground">
      <Bot className="h-4 w-4" /> {t('bubble.sticker')}
    </div>
  )
}

/* ── Thẻ nội dung đặc biệt: danh thiếp / thẻ ngân hàng / link ───────── */

/** Đọc JSON an toàn — nội dung tin nhắn có thể là JSON hoặc text thường */
function safeParse(content: string | null): Record<string, unknown> | null {
  if (!content) return null
  try {
    const o = JSON.parse(content)
    return o && typeof o === 'object' ? (o as Record<string, unknown>) : null
  } catch {
    return null
  }
}

const cardShell = (isSelf: boolean) =>
  cn(
    'w-64 overflow-hidden rounded-2xl border shadow-sm',
    isSelf ? 'rounded-br-sm border-primary/30 bg-primary/10' : 'rounded-bl-sm border-border bg-muted',
  )

/** Danh thiếp Zalo */
function CardBubble({ content, isSelf }: { content: string | null; isSelf: boolean }) {
  const { t } = useTranslation('convChat')
  const o = safeParse(content)
  const userId = String(o?.userId ?? '')
  const phone = o?.phoneNumber ? String(o.phoneNumber) : ''
  if (!userId) return <PlainBubble content={content} isSelf={isSelf} />

  return (
    <div className={cardShell(isSelf)}>
      <div className="flex items-center gap-2 border-b border-border/50 px-3 py-2">
        <IdCard className="h-4 w-4 text-primary" />
        <span className="text-xs font-semibold">{t('bubble.contactCard')}</span>
      </div>
      <div className="space-y-1 px-3 py-2.5 text-sm">
        {phone && (
          <p className="flex items-center gap-1.5">
            <Phone className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="font-medium">{formatPhone(phone)}</span>
          </p>
        )}
        <p className="truncate text-xs text-muted-foreground">UID: {userId}</p>
      </div>
    </div>
  )
}

/**
 * Thẻ bình chọn thật (câu hỏi + lựa chọn + số phiếu) — tải trực tiếp từ Zalo qua
 * `GET /conversations/:id/polls/:pollId` (dùng được cho MỌI bình chọn trong nhóm, không chỉ bình chọn tạo từ
 * eCDP). CHỈ XEM — chưa cho bấm chọn ngay trong eCDP (bỏ phiếu vẫn phải làm trên Zalo), vì đây là yêu cầu
 * hiển thị, bỏ phiếu từ xa là việc khác lớn hơn. Lỗi tải (mất kết nối, bình chọn đã xoá…) thì ẩn hẳn thẻ,
 * giữ lại dòng chip chữ phía trên — không hiện lỗi JSON hay khung trống xấu.
 */
function PollCardBubble({ convId, pollId }: { convId: string; pollId: number }) {
  const { t } = useTranslation('convChat')
  const { data: poll, isLoading, isError } = usePollDetail(convId, pollId)

  if (isLoading) {
    return (
      <div className="flex w-72 items-center justify-center gap-2 rounded-2xl border bg-card px-3 py-4 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        {t('bubble.pollLoading')}
      </div>
    )
  }
  if (isError || !poll) return null

  const maxVotes = Math.max(1, ...poll.options.map((o) => o.votes))

  return (
    <div className="w-72 overflow-hidden rounded-2xl border bg-card shadow-sm">
      <div className="flex items-center gap-2 border-b border-border/50 px-3 py-2">
        <BarChart3 className="h-4 w-4 text-primary" />
        <span className="truncate text-sm font-semibold">{poll.question}</span>
      </div>
      <div className="space-y-2 px-3 py-2.5">
        {poll.options.map((opt) => {
          const pct = poll.num_vote > 0 ? Math.round((opt.votes / maxVotes) * 100) : 0
          return (
            <div key={opt.option_id} className="relative overflow-hidden rounded-lg border border-border/60">
              <div
                className={cn('absolute inset-y-0 left-0', opt.voted ? 'bg-primary/20' : 'bg-muted')}
                style={{ width: `${pct}%` }}
              />
              <div className="relative flex items-center justify-between gap-2 px-2.5 py-1.5 text-sm">
                <span className="flex min-w-0 items-center gap-1.5 truncate">
                  {opt.voted && <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-primary" />}
                  <span className="truncate">{opt.content}</span>
                </span>
                <span className="shrink-0 text-xs text-muted-foreground">{opt.votes}</span>
              </div>
            </div>
          )
        })}
        <p className="pt-0.5 text-[11px] text-muted-foreground">
          {[
            t('bubble.pollVoters', { count: poll.num_vote }),
            poll.is_anonymous && t('bubble.pollAnonymous'),
            poll.closed && t('bubble.pollClosed'),
            t('bubble.pollViewOnly'),
          ].filter(Boolean).join(' · ')}
        </p>
      </div>
    </div>
  )
}

/** Thẻ ngân hàng / số tài khoản */
function BankCardBubble({ content, isSelf }: { content: string | null; isSelf: boolean }) {
  const { t } = useTranslation('convChat')
  const o = safeParse(content)
  const acc = o?.numAccBank ? String(o.numAccBank) : ''
  const name = o?.nameAccBank ? String(o.nameAccBank) : ''
  const bin = o?.binBank != null ? String(o.binBank) : ''
  if (!acc) return <PlainBubble content={content} isSelf={isSelf} />

  return (
    <div className={cardShell(isSelf)}>
      <div className="flex items-center gap-2 border-b border-border/50 px-3 py-2">
        <CreditCard className="h-4 w-4 text-primary" />
        <span className="text-xs font-semibold">{t('bubble.bankInfo')}</span>
      </div>
      <div className="space-y-1.5 px-3 py-2.5">
        <div>
          <p className="text-[11px] text-muted-foreground">{t('bubble.accountNumber')}</p>
          <p className="select-all font-mono text-sm font-semibold tracking-wide">{acc}</p>
        </div>
        {name && (
          <div>
            <p className="text-[11px] text-muted-foreground">{t('bubble.accountHolder')}</p>
            <p className="text-sm font-medium">{name}</p>
          </div>
        )}
        {bin && <p className="text-[11px] text-muted-foreground">{t('bubble.bankCode', { code: bin })}</p>}
      </div>
    </div>
  )
}

/**
 * Video Zalo: `content` là JSON có `href` (video) và `thumb` (ảnh đại diện).
 * Không phát nội tuyến — CDN Zalo hay chặn theo referer; mở tab mới chắc ăn hơn.
 */
function VideoBubble({ view, isSelf }: { view: Extract<MessageView, { kind: 'video' }>; isSelf: boolean }) {
  const secs = view.durationMs ? Math.round(view.durationMs / 1000) : 0
  return (
    <a
      href={safeHttpUrl(view.href)}
      target="_blank"
      rel="noreferrer"
      className={cn(cardShell(isSelf), 'group/vid relative block overflow-hidden')}
    >
      {view.thumb ? (
        <img src={view.thumb} alt="" className="h-44 w-full object-cover" loading="lazy" />
      ) : (
        <div className="flex h-28 w-56 items-center justify-center bg-muted" />
      )}
      <span className="absolute inset-0 flex items-center justify-center">
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-black/55 text-white transition-transform group-hover/vid:scale-110">
          <Play className="ml-0.5 h-5 w-5 fill-current" />
        </span>
      </span>
      {secs > 0 && (
        <span className="absolute bottom-2 right-2 rounded bg-black/65 px-1.5 py-0.5 text-[11px] text-white">
          {Math.floor(secs / 60)}:{String(secs % 60).padStart(2, '0')}
        </span>
      )}
    </a>
  )
}

/** URL trong chữ khách gõ; bỏ dấu câu dính cuối (", . ) ]") khỏi link. */
const URL_IN_TEXT = /https?:\/\/[^\s<>"']+/g
const URL_TRAILING_PUNCT = /[.,;:!?)\]}]+$/

/** Chữ thường + link bấm được (mở tab mới). */
function LinkifiedText({ text }: { text: string }) {
  const parts: ReactNode[] = []
  let last = 0
  for (const m of text.matchAll(URL_IN_TEXT)) {
    const start = m.index ?? 0
    const url = m[0].replace(URL_TRAILING_PUNCT, '')
    if (start > last) parts.push(text.slice(last, start))
    parts.push(
      <a key={start} href={url} target="_blank" rel="noreferrer" className="break-all underline underline-offset-2">
        {url}
      </a>,
    )
    last = start + url.length
  }
  if (last < text.length) parts.push(text.slice(last))
  return <>{parts}</>
}

/**
 * Tin Zalo có link (brief, báo giá…): hiện ĐỦ chữ khách gõ, giữ xuống dòng, link bấm được, kèm thẻ xem trước
 * trang như Zalo. Trước đây bị vẽ thành thẻ cắt còn 2 dòng → nhân viên không đọc được nội dung.
 */
function ZaloLinkBubble({
  view,
  reply,
  isSelf,
}: {
  view: Extract<MessageView, { kind: 'link' }>
  reply?: string | null
  isSelf: boolean
}) {
  const pv = view.preview
  const safeHref = safeHttpUrl(view.href)
  const canOpen = !!safeHref
  return (
    <div
      className={cn(
        'max-w-full overflow-hidden rounded-2xl px-3.5 py-2 text-sm shadow-sm',
        isSelf ? 'rounded-br-sm bg-primary/10 text-foreground' : 'rounded-bl-sm bg-muted text-foreground',
      )}
    >
      {reply && (
        <div
          className={cn(
            'mb-1 border-l-2 px-2 py-1 text-xs opacity-80',
            isSelf ? 'border-primary/40' : 'border-border',
          )}
        >
          {reply}
        </div>
      )}
      <p className="whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
        <LinkifiedText text={view.text} />
      </p>
      {pv && canOpen && (
        <a
          href={safeHref}
          target="_blank"
          rel="noreferrer"
          className={cn(
            'mt-2 block overflow-hidden rounded-lg border transition-opacity hover:opacity-90',
            isSelf ? 'border-primary/20 bg-background' : 'bg-background',
          )}
        >
          {pv.thumb && (
            <img
              src={proxyZaloMedia(pv.thumb) ?? pv.thumb}
              alt=""
              loading="lazy"
              referrerPolicy="no-referrer"
              className="h-28 w-full object-cover"
              onError={(e) => { e.currentTarget.style.display = 'none' }}
            />
          )}
          <div className="space-y-0.5 px-2.5 py-1.5">
            {pv.title && <p className="line-clamp-2 text-xs font-medium">{pv.title}</p>}
            {pv.description && <p className="line-clamp-2 text-[11px] opacity-75">{pv.description}</p>}
            {pv.site && <p className="truncate text-[11px] opacity-75">{pv.site}</p>}
          </div>
        </a>
      )}
      {/* Không có thẻ xem trước mà chữ không chứa link (khách gõ "xtep.vn", link bị Zalo tách) → vẫn phải có chỗ bấm. */}
      {!pv && canOpen && view.href && !view.text.includes(view.href) && (
        <a href={safeHref} target="_blank" rel="noreferrer" className="mt-1 block truncate text-xs underline underline-offset-2">
          {view.href}
        </a>
      )}
    </div>
  )
}

/**
 * Thẻ chung cho các loại Zalo gửi kèm `title`/`description`/`thumb` mà hệ thống
 * chưa có bộ vẽ riêng (lời nhắc, danh thiếp, nội dung chia sẻ…). Có thẻ này thì
 * không loại nào còn rơi xuống nhánh chữ và in ra JSON.
 */
function GenericCardBubble({ view, isSelf }: { view: Extract<MessageView, { kind: 'card' }>; isSelf: boolean }) {
  const inner = (
    <>
      {view.thumb && <img src={view.thumb} alt="" className="h-28 w-full object-cover" loading="lazy" />}
      <div className="space-y-0.5 px-3 py-2">
        <p className="line-clamp-2 text-sm font-medium">{view.title}</p>
        {view.description && (
          <p className="line-clamp-3 text-xs text-muted-foreground">{view.description}</p>
        )}
      </div>
    </>
  )
  // Chỉ mở link khi thực sự là link xem được, không mở ảnh minh hoạ của Zalo.
  const safeHref = view.href !== view.thumb ? safeHttpUrl(view.href) : undefined
  const isLink = !!safeHref
  return isLink ? (
    <a href={safeHref} target="_blank" rel="noreferrer" className={cn(cardShell(isSelf), 'block hover:opacity-90')}>
      {inner}
    </a>
  ) : (
    <div className={cn(cardShell(isSelf))}>{inner}</div>
  )
}

/** Link có xem trước — nội dung Zalo trả về rất dài, chỉ lấy phần cần thiết */
function LinkBubble({ content, isSelf }: { content: string | null; isSelf: boolean }) {
  const o = safeParse(content)
  const href = String(o?.href ?? o?.link ?? '')
  const title = String(o?.title ?? '')
  const desc = String(o?.description ?? o?.desc ?? '')
  const thumb = String(o?.thumb ?? o?.thumbUrl ?? '')
  if (!href && !title) return <PlainBubble content={content} isSelf={isSelf} />

  return (
    <a
      href={safeHttpUrl(href)}
      target="_blank"
      rel="noreferrer"
      className={cn(cardShell(isSelf), 'block transition-opacity hover:opacity-90')}
    >
      {thumb && (
        <img src={thumb} alt="" className="h-28 w-full object-cover" loading="lazy" />
      )}
      <div className="space-y-0.5 px-3 py-2">
        <p className="line-clamp-2 text-sm font-medium">{title || href}</p>
        {desc && <p className="line-clamp-2 text-xs text-muted-foreground">{desc}</p>}
        {href && <p className="truncate text-[11px] text-primary">{href}</p>}
      </div>
    </a>
  )
}

/** Dự phòng khi không đọc được cấu trúc — hiện text gọn thay vì JSON thô */
function PlainBubble({ content, isSelf }: { content: string | null; isSelf: boolean }) {
  const { t } = useTranslation('convChat')
  return (
    <div
      className={cn(
        'rounded-2xl px-3.5 py-2 text-sm shadow-sm',
        isSelf ? 'rounded-br-sm bg-primary/10 text-foreground' : 'rounded-bl-sm bg-muted text-foreground',
      )}
    >
      {content && content.trim().startsWith('{') ? t('bubble.specialContent') : content}
    </div>
  )
}

/** 84xxxxxxxxx → 0xxx xxx xxx cho dễ đọc */
function formatPhone(raw: string): string {
  const digits = raw.replace(/\D/g, '')
  const local = digits.startsWith('84') ? `0${digits.slice(2)}` : digits
  return local.replace(/(\d{4})(\d{3})(\d{3})/, '$1 $2 $3')
}

const SOURCE_ICON: Record<SourceIcon, typeof Bot> = {
  staff: UserRound, ai: Bot, automation: Zap, outside: Smartphone, zns: Mail, unknown: HelpCircle,
}

/** "· AI Lan" / "· Ngọc" / "· Tự động · Chào ngoài giờ" / "· Gửi ngoài eCDP" cạnh giờ của tin gửi ra. */
function SourceTag({ message }: { message: ChatMessage }) {
  // Tin AI cũ không ghi bot → tên nhân viên AI mặc định (thay "AI chung"). Danh sách bot dùng chung cache.
  useTranslation('convChat') // vẽ lại khi đổi ngôn ngữ (chữ nguồn lấy từ i18n)
  const { data: bots } = useAiBots()
  const cap = sourceCaption(sourceOf(message), { defaultAiName: defaultAiName(bots?.bots) })
  if (!cap) return null
  const Icon = SOURCE_ICON[cap.icon]
  return (
    <span
      className={cn(
        'inline-flex max-w-[16rem] items-center gap-1 truncate',
        cap.icon === 'ai' && 'text-primary',
        cap.icon === 'outside' && 'text-amber-600 dark:text-amber-400',
      )}
      title={cap.title}
    >
      <span aria-hidden>·</span>
      <Icon className="h-3 w-3 shrink-0" />
      <span className="truncate">{cap.text}</span>
    </span>
  )
}
